import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import comptabiliteService, { EtatResultat as EtatResultatData, Rubrique } from '../../api/comptabilite';
import { formatTND } from '../../utils/formatters';

const startOfYear = () => `${new Date().getFullYear()}-01-01`;
const today = () => new Date().toISOString().slice(0, 10);

const RubriqueBlock: React.FC<{ rubrique: Rubrique; sign?: '+' | '−' }> = ({ rubrique, sign }) => (
  <div className="mb-4">
    <div className="flex justify-between font-semibold text-gray-800 border-b border-gray-200 pb-1">
      <span>{rubrique.libelle}</span>
      <span>{sign === '−' ? '(' : ''}{formatTND(rubrique.total)}{sign === '−' ? ')' : ''}</span>
    </div>
    {rubrique.lignes.length === 0 ? (
      <div className="text-xs text-gray-400 py-1">—</div>
    ) : rubrique.lignes.map((l) => (
      <div key={l.numero} className="flex justify-between text-sm text-gray-600 py-0.5">
        <span><span className="font-mono text-gray-400 mr-2">{l.numero}</span>{l.libelle}</span>
        <span>{formatTND(l.montant)}</span>
      </div>
    ))}
  </div>
);

const Line: React.FC<{ label: string; value: number; strong?: boolean }> = ({ label, value, strong }) => (
  <div className={`flex justify-between py-2 px-3 rounded-lg ${strong ? 'bg-blue-50 font-bold text-blue-900' : 'font-semibold text-gray-800'}`}>
    <span>{label}</span>
    <span className={value < 0 ? 'text-red-600' : ''}>{formatTND(value)}</span>
  </div>
);

const EtatResultat: React.FC = () => {
  const [startDate, setStartDate] = useState(startOfYear());
  const [endDate, setEndDate] = useState(today());
  const [data, setData] = useState<EtatResultatData | null>(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setData(await comptabiliteService.etatResultat(startDate, endDate));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [startDate, endDate]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">État de résultat</h1>
        <p className="text-sm text-gray-500">Produits et charges de la période (modèle SCE tunisien).</p>
      </div>

      <div className="flex flex-wrap items-end gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">Du</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
                 className="rounded-lg border border-gray-200 px-3 py-2" />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Au</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
                 className="rounded-lg border border-gray-200 px-3 py-2" />
        </div>
      </div>

      {loading ? (
        <div className="text-center text-gray-400 py-10">Chargement…</div>
      ) : !data ? null : (
        <div className="bg-white p-6 rounded-xl border border-gray-200 max-w-2xl space-y-2">
          <RubriqueBlock rubrique={data.produitsExploitation} />
          <RubriqueBlock rubrique={data.chargesExploitation} sign="−" />
          <Line label="Résultat d'exploitation" value={data.resultatExploitation} />

          <div className="pt-3">
            <RubriqueBlock rubrique={data.produitsFinanciers} />
            <RubriqueBlock rubrique={data.chargesFinancieres} sign="−" />
            <Line label="Résultat financier" value={data.resultatFinancier} />
          </div>

          <Line label="Résultat des activités ordinaires avant impôt" value={data.resultatAvantImpot} />
          <div className="flex justify-between py-2 px-3 text-sm text-gray-600">
            <span>Impôt sur les bénéfices</span>
            <span>({formatTND(data.impot)})</span>
          </div>
          <Line label="Résultat net de l'exercice" value={data.resultatNet} strong />
        </div>
      )}
    </div>
  );
};

export default EtatResultat;
